<template>
  <div class="auth-card-wrapper">
    <div class="auth-card">
      <div class="auth-card-container">
        <brand-logo class="logo auth-logo" />
        <template v-if="$route.query.thanks">
          <h3 class="text-h5 font-weight-bold pb-4 text-center">Your account is now enabled for login.</h3>
          <p class="text-center">
            Contact us at
            <a :href="`mailto:${brand.emails.support}`">{{ brand.emails.support }}</a> with
            <br/>any questions, suggestions or feedback!
          </p>
        </template>
        <template v-else>
          <h3 class="text-title">Login to your account</h3>
        </template>
      </div>

      <!-- MFA Verification Screen -->
      <template v-if="mfaChallenge">
        <div class="auth-card-container" data-cy="mfa-challenge-screen">
          <h3 class="text-title">Two-Factor Authentication</h3>
          <p class="text-body-2 mb-4">Enter the 6-digit code from your authenticator app.</p>
        </div>
        <v-form ref="mfaForm" @submit.prevent="submitMfa">
          <div class="auth-card-container form-fields" style="margin-bottom: 8px">
            <v-text-field
              name="mfa-code"
              label="Authentication Code"
              v-model="mfaCode"
              :rules="[rules.required, rules.mfaCode]"
              maxlength="6"
              autofocus
              variant="outlined"
              density="default"
              prepend-inner-icon="mdi-shield-key"
              data-cy="mfa-code-input"
            ></v-text-field>

            <p class="message bg-error" v-if="error" data-cy="login-error">{{ error }}</p>
          </div>

          <div class="auth-card-container">
            <div class="auth-card-footer justify-space-between">
              <div class="auth-card-footer-part">
                <a class="text text-decoration-none" style="cursor: pointer" @click="cancelMfa">
                  Back to login
                </a>
              </div>
              <div class="auth-card-footer-part">
                <v-btn rounded="lg" size="large" color="primary" class="ml-auto d-flex" variant="flat" type="submit"
                       tabindex="1" data-cy="mfa-submit-btn">
                  Verify
                </v-btn>
              </div>
            </div>
          </div>
        </v-form>
      </template>

      <!-- Normal Login Form -->
      <template v-else>
        <v-form ref="form" v-model="valid">
          <div class="auth-card-container form-fields" style="margin-bottom: 8px">
            <v-text-field
              name="email"
              label="Email Address"
              type="email"
              ref="username"
              v-model="email"
              :rules="[rules.required, rules.email]"
              v-on:keyup.enter="submit"
              tabindex="1"
              autocomplete="username"
              variant="outlined"
              density="default"
              data-cy="email-input"
            ></v-text-field>

            <v-text-field
              name="password"
              label="Password"
              id="password"
              ref="password"
              v-model="password"
              :append-inner-icon="e1 ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
              @click:append-inner="() => (e1 = !e1)"
              :type="e1 ? 'password' : 'text'"
              min="8"
              :rules="[rules.required]"
              counter
              v-on:keyup.enter="submit"
              tabindex="2"
              autocomplete="current-password"
              variant="outlined"
              density="default"
              data-cy="password-input"
            ></v-text-field>

            <v-alert v-if="$route.query.resetPasswordSuccess" type="success" variant="tonal" density="compact" class="mb-2">
              Password reset. You can now log in.
            </v-alert>
            <v-alert v-else-if="$route.query.reason === 'password_changed'" type="success" variant="tonal" density="compact" class="mb-2">
              Password changed. Please log in again. Other active sessions will expire within 5 minutes.
            </v-alert>
            <v-alert v-else-if="$route.query.reason === 'global_logout'" type="info" variant="tonal" density="compact" class="mb-2">
              All sessions revoked. Please log in again.
            </v-alert>
            <p v-else-if="$route.query.alreadyConfirmed" class="message bg-error">Account already verified.</p>

            <p class="message bg-error" v-if="error" data-cy="login-error">{{ error }}</p>
            <p class="message bg-indigo" v-if="message">{{ message }}</p>
          </div>

          <div class="auth-card-container">
            <div class="auth-card-footer justify-space-between">
              <div class="auth-card-footer-part auth-card-footer-part_column">
                <router-link :to="{ path: '/reset-password' }" class="text text-decoration-none">
                  Forgot password?
                </router-link>
                <router-link :to="{ path: '/confirm' }" class="text text-decoration-none">
                  Got a registration code?
                </router-link>
              </div>
              <div class="auth-card-footer-part">
                <p class="text">
                  No account?
                  <router-link
                    class="text text-decoration-none"
                    tabindex="5"
                    :to="{ path: '/signup', query: { projectId: $route.query.projectId || undefined, redirect: $route.query.redirect || undefined }  }"
                  >
                    <span class="light--text">Sign up</span>
                  </router-link>
                </p>
                <v-btn rounded="lg" size="large" color="primary" class="ml-auto d-flex" variant="flat" @click="submit"
                       tabindex="3">
                  Log in
                </v-btn>
              </div>
            </div>
          </div>
        </v-form>

        <div class="auth-card-container">
          <text-divider text="or"/>
          <google-sign-in-button/>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import { authHelpers, parseAuthCustomState } from '@/api/authHelpers'
import { authProxyApi } from '@/api/authProxyApi'
import { safeRedirectPath } from '@/utils/loginRedirect'
import GoogleSignInButton from '@/components/GoogleSignInButton.vue'
import TextDivider from '@/components/TextDivider.vue'
import { Auth } from '@aws-amplify/auth'
import BrandLogo from '@/components/BrandLogo.vue'
import brand from '@/composables/useBrand'

export default {
  components: {
    BrandLogo,
    TextDivider,
    GoogleSignInButton
  },
  computed: {
    brand: () => brand
  },
  data () {
    return {
      valid: true,
      email: '',
      password: '',
      error: null,
      message: null,
      mfaChallenge: null,
      mfaCode: '',
      mfaSubmitting: false,
      rules: {
        required: (value) => !!value || 'Required.',
        email: (value) => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Invalid e-mail.'
        },
        mfaCode: (value) => /^\d{6}$/.test(value) || 'Enter a 6-digit code.'
      },
      e1: true
    }
  },
  created () {
    if (this.$route.query.username !== '') {
      this.email = this.$route.query.username
    }
  },
  async mounted () {
    if (this.$route.query.thanks) {
      // window.gtag('event', 'conversion', { send_to: 'AW-676520547/ZI1ACJashMwBEOPEy8IC' })
    }
    // Removed snackbars in favor of prominent static v-alert notices
    if (this.$route.query.username) {
      this.$refs.password.focus()
    }
    if (Auth && this.$route.query.googleSignIn) {
      try {
        const currentUser = await Auth.currentAuthenticatedUser()
        if (currentUser) {
          try {
            this.$loadingState.start()

            const sessionIdInfo = currentUser.signInUserSession.idToken.payload
            await this.initializeSession(sessionIdInfo)
          } catch (err) {
            this.$loadingState.stop()
            this.error = err.message.replace('GraphQL error: ', '')
            authHelpers.logoutAndClearStore()
          }
        }
      } catch (e) {
        console.log(e)
      }
    }
  },
  methods: {
    async updateUser () {
      const session = await this.$cognitoAuth.getSession()
      const sessionIdInfo = session.getIdToken().payload
      const currentUser = await Auth.currentAuthenticatedUser()

      if (sessionIdInfo?.identities?.[0]?.providerName === 'GLHOMES') {
        await Auth.updateUserAttributes(currentUser, {
          'custom:organisation': 'GL Homes'
        })
      }

      const attributes = await this.$cognitoAuth.getUserAttributes()
      const indexCompany = attributes.findIndex(item => item.Name === 'custom:organisation')
      const company = indexCompany >= 0 ? attributes[indexCompany].Value : null
      const indexReferralSource = attributes.findIndex(item => item.Name === 'custom:referral_source')
      const referralSource = indexReferralSource >= 0 ? attributes[indexReferralSource].Value : null
      const indexUsageIntent = attributes.findIndex(item => item.Name === 'custom:usage_intent')
      const usageIntent = indexUsageIntent >= 0 ? attributes[indexUsageIntent].Value : null
      const indexFirstname = attributes.findIndex(item => item.Name === 'given_name')
      const firstname = attributes[indexFirstname].Value
      const indexLastname = attributes.findIndex(item => item.Name === 'family_name')
      const lastname = attributes[indexLastname].Value
      const indexEmail = attributes.findIndex(item => item.Name === 'email')
      const email = attributes[indexEmail].Value
      const indexPhoneNumber = attributes.findIndex(item => item.Name === 'phone_number')
      const phoneNumber = indexPhoneNumber >= 0 ? attributes[indexPhoneNumber].Value : null
      const queryResult = await store.dispatch('user/getCurrentUser')
      const variables = {
        firstname,
        lastname,
        email,
        company,
        referralSource,
        usageIntent,
        phoneNumber,
        increaseLoginCounter: true
      }
      if (queryResult.getCurrentUser) {
        await store.dispatch('user/updateUser', variables)
      } else {
        await store.dispatch('user/createUser', variables)
        window.trackSignupCompleted()
        if (company) {
          await store.dispatch('organisations/createOrganisation', { name: company })
        }
        await store.dispatch('teamMembers/convertInvitesToTeamMembers')
        this.$store.dispatch('projectMemberships/listProjectMembershipsForCurrentUser')
      }
    },
    async submit () {
      const { valid } = await this.$refs.form.validate()
      if (valid) {
        try {
          this.$loadingState.start()
          const email = (this.email || '').toLowerCase()

          const res = await authProxyApi.login({
            email,
            password: this.password
          })

          // Unconfirmed account — route to /confirm with auto-resend so the
          // user can finish verification instead of being stuck on "Invalid
          // email or password" forever.
          if (res?.code === 'EMAIL_NOT_VERIFIED') {
            this.$loadingState.stop()
            this.$router.replace({
              path: '/confirm',
              query: {
                username: email,
                autoResend: true,
                projectId: this.$route.query.projectId || undefined,
                redirect: this.$route.query.redirect || undefined
              }
            })
            return
          }

          // MFA challenge returned - show MFA input screen
          if (res?.ok && res?.mfaRequired) {
            this.mfaChallenge = {
              session: res.session,
              email
            }
            this.error = null
            this.$loadingState.stop()
            return
          }

          if (!res?.ok || !res?.tokens?.idToken || !res?.tokens?.accessToken) {
            throw new Error(res?.message || 'Authentication failed. Please try again.')
          }

          this.$cognitoAuth.setSessionFromTokens(email, res.tokens)

          const session = await this.$cognitoAuth.getSession()
          const sessionIdInfo = session.getIdToken().payload

          await this.initializeSession(sessionIdInfo)
        } catch (err) {
          console.log(err)
          this.$loadingState.stop()
          this.error = err?.message || 'Authentication failed. Please try again.'
          authHelpers.logoutAndClearStore()
        }
      }
    },
    async submitMfa () {
      if (this.mfaSubmitting) return
      this.mfaSubmitting = true

      const { valid } = await this.$refs.mfaForm.validate()
      if (!valid) {
        this.mfaSubmitting = false
        return
      }

      try {
        this.$loadingState.start()
        this.error = null

        const res = await authProxyApi.mfaVerify({
          email: this.mfaChallenge.email,
          code: this.mfaCode,
          session: this.mfaChallenge.session
        })

        // Wrong code — stay on the MFA screen, clear the input, surface the
        // message. Without this branch the catch below would call
        // redirectToLoginForm() and drop the user back at the email/password
        // form, forcing them to re-enter their password from scratch.
        if (res?.code === 'MFA_CODE_INVALID') {
          this.$loadingState.stop()
          this.error = res.message
          this.mfaCode = ''
          return
        }

        if (!res?.ok || !res?.tokens?.idToken || !res?.tokens?.accessToken) {
          this.mfaCode = ''
          throw new Error(res?.message || 'Authentication failed. Please try again.')
        }

        this.$cognitoAuth.setSessionFromTokens(this.mfaChallenge.email, res.tokens)

        const session = await this.$cognitoAuth.getSession()
        const sessionIdInfo = session.getIdToken().payload

        await this.initializeSession(sessionIdInfo)
      } catch (err) {
        console.log(err)
        this.$loadingState.stop()
        this.error = err?.message || 'Authentication failed. Please try again.'
        this.redirectToLoginForm()
      } finally {
        this.mfaSubmitting = false
      }
    },
    redirectToLoginForm () {
      // Setting mfaChallenge to null hides the MFA <template v-if> and
      // shows the email/password <template v-else>, effectively "redirecting"
      // the user back to the login form on the same page.
      //
      // Also clear any pre-existing Cognito session in localStorage. Otherwise
      // a failed/cancelled MFA can leave a stale (still-valid) session from a
      // prior login intact, which the router would honor on the next navigation
      // — letting a wrong/expired TOTP code effectively "succeed" via the
      // lingering tokens.
      authHelpers.logoutAndClearStore()
      this.mfaChallenge = null
      this.mfaCode = ''
    },
    cancelMfa () {
      this.redirectToLoginForm()
      this.error = null
    },
    async initializeSession (sessionIdInfo) {
      let adminUser = false
      if (sessionIdInfo['cognito:groups']) {
        if (sessionIdInfo['cognito:groups'][0] === 'Admin') {
          adminUser = true
        }
      }

      const state = parseAuthCustomState(this.$route.query.state)
      const projectId = this.$route.query.projectId || state?.projectId || null

      store.commit('login', { email: sessionIdInfo.email, adminUser, userId: sessionIdInfo['cognito:username'] })
      await this.updateUser()
      this.$loadingState.stop()
      const redirect = safeRedirectPath(this.$route.query.redirect || state?.redirect)
      await this.$router.replace(redirect || `/dashboard/${projectId || ''}`)
    }
  }
}
</script>

<style scoped>
.text {
  color: var(--text-dark);
  font-size: 13px;
  text-align: left;
  margin-bottom: 0;
  line-height: 14.3px;
}

.text-decoration-none {
  text-decoration: none;
}

.logo {
  width: 110px;
  margin-top: 9px;
  margin-bottom: 30px;
}

.form-fields {
  gap: 12px;
  display: flex;
  flex-direction: column;
}

.message {
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
</style>
